import {useState} from 'react';
import {useLocation, Link} from 'react-router-dom';
import {ReactComponent as Logo} from 'assets/logo.svg';
import {Tab, Tabs} from '@mui/material';
import RoleGuard from 'commons/guards/roleGuard';
import {useTranslation} from 'react-i18next';
import './Menu.scss';

interface DataI {
  ItemMenu: JSX.Element;
}

interface Props {
  Menu: DataI[];
}

const Menu = ({Menu}: Props) => {
  const {t} = useTranslation();
  const location = useLocation();
  const {pathname} = location;
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    {
      label: t('Menu.dashboard'),
      path: '/private/palowan/leaves/dashboard',
      keyword: 'dashboard',
    },
    {
      label: t('Menu.leaves'),
      path: '/private/palowan/leaves/my-leaves',
      keyword: 'leaves',
    },
    {
      label: t('Menu.leavesManagement'),
      path: '/private/management/additional-leave',
      keyword: 'management',
      roles: {
        hideForPalowan: true,
      },
    },
  ];

  const getSelectedTabIndex = () => {
    const selectedIndex = menuItems.findIndex(
      item => pathname.indexOf(item.keyword) > -1
    );
    return selectedIndex === -1 ? 0 : selectedIndex;
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <>
      <nav className="Menu flex flex-wrap items-center justify-between px-4 py-3 bg-white">
        <div className="pr-4 flex flex-1 items-center justify-between relative">
          {/* Hamburger button — visible only below lg, left corner */}
          <button
            className="HamburgerBtn lg:hidden flex flex-col justify-center items-center gap-1.5 p-2"
            aria-label="Open menu"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="block w-6 h-0.5 bg-gray-700" />
            <span className="block w-6 h-0.5 bg-gray-700" />
            <span className="block w-6 h-0.5 bg-gray-700" />
          </button>

          {/* Logo — centred on mobile/tablet, left-aligned on desktop */}
          <div className="Logo absolute left-1/2 transform -translate-x-1/2 lg:static lg:transform-none flex items-center">
            <Logo className="w-44 h-9 leading-relaxed inline-block whitespace-nowrap" />
          </div>

          {/* Spacer to balance hamburger on mobile */}
          <div className="lg:hidden w-10" aria-hidden="true" />

          {/* Desktop tab nav — hidden below lg */}
          <div className={'hidden lg:flex flex-grow items-center'}>
            <Tabs value={getSelectedTabIndex()}>
              {menuItems.map(item => (
                <RoleGuard key={item.path} {...item.roles}>
                  <Link to={item.path} className="nav-link">
                    <Tab label={<div>{item.label}</div>} />
                  </Link>
                </RoleGuard>
              ))}
            </Tabs>
            <ul className="flex flex-col lg:flex-row list-none lg:ml-auto">
              {Menu.map((menuItem, index) => (
                <li key={`menu-item-${index}`} className="nav-item">
                  {menuItem.ItemMenu}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </nav>

      {/* Sidebar overlay — visible below lg when open */}
      {sidebarOpen && (
        <div
          className="Sidebar-overlay fixed inset-0 z-40 bg-black bg-opacity-40 lg:hidden"
          onClick={closeSidebar}
          aria-hidden="true"
        />
      )}

      <aside
        className={`Sidebar fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-xl flex flex-col lg:hidden transform transition-transform duration-300 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <button
            className="p-2 text-gray-600 hover:text-gray-900"
            aria-label="Close menu"
            onClick={closeSidebar}
          >
            &#x2715;
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          <ul className="flex flex-col">
            {menuItems.map(item => (
              <RoleGuard key={item.path} {...(item.roles ?? {})}>
                <li>
                  <Link
                    to={item.path}
                    className={`block px-6 py-3 text-sm font-medium hover:bg-gray-100 ${
                      pathname.indexOf(item.keyword) > -1
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-700'
                    }`}
                    onClick={closeSidebar}
                  >
                    {item.label}
                  </Link>
                </li>
              </RoleGuard>
            ))}
          </ul>
        </nav>

        <div className="border-t px-4 py-3 flex flex-col gap-2">
          {Menu.map((menuItem, index) => (
            <div key={`sidebar-item-${index}`}>{menuItem.ItemMenu}</div>
          ))}
        </div>
      </aside>
    </>
  );
};

export default Menu;
